import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from "@mui/material";

import {
  Article,
  ArrowBack,
  ArrowForward,
  Business,
  Close,
  Download,
  Event,
  ExpandMore,
  LocationOn,
  Person,
  Refresh,
  Search,
  Visibility,
  Work,
} from "@mui/icons-material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import {
  Candidate,
  getCandidateCV,
  getCandidatesByJobId,
  getJobById,
  getJobsList,
  JobDetail,
  JobLS,
  previewCandidateCV,
  updateCandidateStatus,
  downloadSelectedCVs,
} from "../Services/jobCandidateService";
import axios from "axios";

type Order = "asc" | "desc";

type OrderBy =
  | "name"
  | "location"
  | "currentDesignation"
  | "currentOrganization"
  | "status"
  | "totalScore"
  | "submitted";

interface EvaluationCriterion {
  name: string;
  weight: number;
  description: string;
}

interface LoadingState {
  jobs: boolean;
  jobDetail: boolean;
  candidates: boolean;
  cv: boolean;
}

interface ErrorState {
  jobs: string;
  jobDetail: string;
  candidates: string;
  cv: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const jobFilter = createFilterOptions<JobLS>({
  stringify: (option) =>
    `${option.jobTitle || ""} ${option.status || ""} ${option.jobId || ""}`,
  trim: true,
});

interface CandidateTrackingPageProps {
  initialJobId?: string;
  initialJobStatus?: string;
}

const normalizeJobStatus = (status?: string) => {
  const value = (status || "").toLowerCase().replace(/\s+/g, "");
  if (value === "open") return "Open";
  if (value === "inreview" || value === "processing") return "In Review";
  if (value === "closed" || value === "reviewed") return "Reviewed";
  return "";
};

const CandidateTrackingPage: React.FC<CandidateTrackingPageProps> = ({
  initialJobId,
  initialJobStatus,
}) => {
  const [jobsList, setJobsList] = useState<JobLS[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobInput, setSelectedJobInput] = useState("");

  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const sortedCandidates = candidates;
  const [stats, setStats] = useState({
  total: 0,
  ranked: 0,
  pendingRanking: 0,
  shortlisted: 0,
  rejected: 0,
  averageScore: 0
});
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [evaluationCriteria, setEvaluationCriteria] = useState<
    EvaluationCriterion[]
  >([]);

  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [candidateStatusFilter, setCandidateStatusFilter] = useState("All");

  const [search, setSearch] = useState("");
  const [serverSearch, setServerSearch] = useState("");

  const [expandedCandidateIds, setExpandedCandidateIds] = useState<string[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<OrderBy>("totalScore");

  const [downloadingCandidateId, setDownloadingCandidateId] = useState<
    string | null
  >(null);

  const [previewCandidateId, setPreviewCandidateId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewMimeType, setPreviewMimeType] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const previewRequestId = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [paginationData, setPaginationData] = useState({
    totalCount: 0,
    pageNumber: 1,
    pageSize: 25,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const [loading, setLoading] = useState<LoadingState>({
    jobs: false,
    jobDetail: false,
    candidates: false,
    cv: false,
  });

  const [error, setError] = useState<ErrorState>({
    jobs: "",
    jobDetail: "",
    candidates: "",
    cv: "",
  });

  const setLoadingState = (key: keyof LoadingState, value: boolean) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  const setErrorState = (key: keyof ErrorState, value: string) => {
    setError((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!initialJobId) return;
    const status = normalizeJobStatus(initialJobStatus);
    setJobStatusFilter(status);
    setSelectedJobId(initialJobId);
    if (status) fetchJobs(status);
  }, [initialJobId, initialJobStatus]);

  const parseEvaluationCriteria = (value?: string): EvaluationCriterion[] => {
    try {
      if (!value) return [];

      const parsed = JSON.parse(value);
      const normalized =
        typeof parsed === "string" ? JSON.parse(parsed) : parsed;

      return normalized?.criteria || [];
    } catch {
      return [];
    }
  };

  const parseCriteriaScores = (
    value?: string,
  ): Record<
    string,
    {
      score?: number;
      Score?: number;
      matchPercentage?: number;
      MatchPercentage?: number;
    }
  > => {
    try {
      if (!value) return {};

      const parsed = JSON.parse(value);
      const normalized =
        typeof parsed === "string" ? JSON.parse(parsed) : parsed;

      return normalized || {};
    } catch {
      return {};
    }
  };

  /**
   * Fix for answers showing as JSON array in first question.
   *
   * Handles all possible backend shapes:
   * 1. ["answer 1", "answer 2"]
   * 2. ["[\"answer 1\", \"answer 2\"]"]
   * 3. "[\"answer 1\", \"answer 2\"]"
   * 4. "single answer"
   */
  const normalizeAnswers = (answers?: string[] | string): string[] => {
    if (!answers) return [];

    if (Array.isArray(answers)) {
      if (answers.length === 1 && typeof answers[0] === "string") {
        const first = answers[0].trim();

        if (first.startsWith("[") && first.endsWith("]")) {
          try {
            const parsed = JSON.parse(first);
            return Array.isArray(parsed)
              ? parsed.map((x) => String(x ?? ""))
              : answers;
          } catch {
            return answers;
          }
        }
      }

      return answers.map((x) => String(x ?? ""));
    }

    if (typeof answers === "string") {
      const text = answers.trim();

      if (text.startsWith("[") && text.endsWith("]")) {
        try {
          const parsed = JSON.parse(text);
          return Array.isArray(parsed)
            ? parsed.map((x) => String(x ?? ""))
            : [answers];
        } catch {
          return [answers];
        }
      }

      return [answers];
    }

    return [];
  };

  const getCandidateAnswer = (candidate: Candidate, index: number) => {
    const normalizedAnswers = normalizeAnswers(candidate.answers);
    return normalizedAnswers[index] || "No answer provided.";
  };

  const getScoreValue = (
    candidate: Candidate,
    criterionName: string,
  ): number => {
    const scores = parseCriteriaScores(candidate.criteriaScore);

    return scores[criterionName]?.score ?? scores[criterionName]?.Score ?? 0;
  };

  const getApplicationStageColor = (
    status?: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    const normalized = (status || "").toLowerCase();

    if (normalized === "shortlisted") return "primary";
    if (normalized === "finalized") return "success";
    if (normalized === "rejected") return "error";
    if (normalized === "applied") return "info";
    if (normalized === "ranked") return "success";
    if (normalized === "ranking failed") return "error";

    return "default";
  };

  const getRankingChip = (candidate: Candidate) => {
    if (!candidate.isRanked) {
      return <Chip size="small" label="Pending" variant="outlined" />;
    }

    if ((candidate.status || "").toLowerCase() === "ranking failed") {
      return (
        <Chip size="small" label="Failed" color="error" variant="outlined" />
      );
    }

    const score = candidate.totalScore || 0;

    const color =
      score >= 80
        ? "success"
        : score >= 60
          ? "info"
          : score >= 40
            ? "warning"
            : "error";

    return (
      <Chip
        size="small"
        label={`${score}/100`}
        color={color}
        sx={{ fontWeight: 900 }}
      />
    );
  };

  const formatWeight = (weight?: number) =>
    `${Math.round((weight || 0) * 100)}%`;

  const getInitials = (name?: string) => {
    if (!name) return "?";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0]?.toUpperCase())
      .join("");
  };

  const fetchJobs = async (status: string) => {
    if (!status) return;

    try {
      setLoadingState("jobs", true);
      setErrorState("jobs", "");

      const data = await getJobsList(status);
      setJobsList(data || []);
    } catch (err) {
      setErrorState(
        "jobs",
        err instanceof Error ? err.message : "Failed to fetch jobs.",
      );
      setJobsList([]);
    } finally {
      setLoadingState("jobs", false);
    }
  };

  // Candidate list is independent of job-detail loading.
  // Requests are cancelled when filters, sorting or the job change.
  const fetchCandidates = async (signal?: AbortSignal) => {
    const jobId = selectedJobId;
    if (!jobId) return;

    try {
      setLoadingState("candidates", true);
      setErrorState("candidates", "");

      const result = await getCandidatesByJobId(
        jobId,
        paginationData.pageNumber,
        paginationData.pageSize,
        candidateStatusFilter === "All" ? undefined : candidateStatusFilter,
        serverSearch || undefined,
        orderBy,
        order,
        signal,
      );

      if (signal?.aborted) return;
      setCandidates(result.items ?? []);
      setStats(result.stats ?? {
        total: 0,
        ranked: 0,
        pendingRanking: 0,
        shortlisted: 0,
        rejected: 0,
        averageScore: 0,
      });
      setPaginationData(prev => ({
        ...prev,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        hasPreviousPage: result.hasPreviousPage,
        hasNextPage: result.hasNextPage,
      }));
    } catch (err) {
      if (axios.isCancel(err) || signal?.aborted) return;
      console.error("Candidate loading failed:", err);
      setErrorState("candidates", "Unable to load candidates. Please try again.");
      setCandidates([]);
    } finally {
      if (!signal?.aborted) setLoadingState("candidates", false);
    }
  };

  useEffect(() => {
  if (!selectedJobId) {
    setJobDetail(null);
    setCandidates([]);
    setEvaluationCriteria([]);
    return;
  }

  let active = true;

  // Clear previous job data immediately
  setCriteriaOpen(false);
  setJobDetail(null);
  setEvaluationCriteria([]);

  setLoadingState("jobDetail", true);
  setErrorState("jobDetail", "");

  getJobById(selectedJobId)
    .then((data) => {
      if (!active) return;

      setJobDetail(data);

      setEvaluationCriteria(
        parseEvaluationCriteria(data.evaluationCriteria)
      );
    })
    .catch((err) => {
      if (!active) return;

      console.error("Failed to load job details:", err);

      setErrorState(
        "jobDetail",
        "Failed to load job details."
      );

      setJobDetail(null);
      setEvaluationCriteria([]);
    })
    .finally(() => {
      if (active) {
        setLoadingState("jobDetail", false);
      }
    });

  // Cleanup when another job is selected
  return () => {
    active = false;
  };
}, [selectedJobId]);

  useEffect(() => {
    if (!selectedJobId) {
      setCandidates([]);
      setStats({
        total: 0, ranked: 0, pendingRanking: 0,
        shortlisted: 0, rejected: 0, averageScore: 0,
      });
      return;
    }

    const controller = new AbortController();
    void fetchCandidates(controller.signal);
    return () => controller.abort();
  }, [
    selectedJobId,
    paginationData.pageNumber,
    paginationData.pageSize,
    candidateStatusFilter,
    serverSearch,
    orderBy,
    order,
    refreshKey,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPaginationData((prev) => ({ ...prev, pageNumber: 1 }));
      setServerSearch(search.trim());
    }, 450);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    previewRequestId.current += 1;
    setPreviewCandidateId(null);
    setPreviewUrl("");
    setPreviewLoading(false);
    setPreviewError("");
  }, [selectedJobId, paginationData.pageNumber, paginationData.pageSize, serverSearch, orderBy, order]);


const handleSort = (field: OrderBy) => {
  if (orderBy === field) {
    setOrder(prev => prev === "asc" ? "desc" : "asc");
  } else {
    setOrderBy(field);
    setOrder(field === "totalScore" ? "desc" : "asc");
  }

  setPaginationData(prev => ({
    ...prev,
    pageNumber: 1
  }));

  setExpandedCandidateIds([]);
  setSelectedCandidates([]);
};
  const resetCandidateFilters = () => {
    setSearch("");
    setServerSearch("");
    setCandidateStatusFilter("All");
    setExpandedCandidateIds([]);
    setSelectedCandidates([]);
    setPaginationData((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleJobStatusChange = (event: SelectChangeEvent) => {
    const status = event.target.value;

    setJobStatusFilter(status);
    setSelectedJobId(null);
    setSelectedJobInput("");
    setJobDetail(null);
    setCandidates([]);
    setJobsList([]);
    resetCandidateFilters();

    if (status) {
      fetchJobs(status);
    }
  };

  const handleJobChange = (job: JobLS | null) => {
    setSelectedJobId(job?.jobId || null);
    resetCandidateFilters();
  };

  const handleCandidateStatusChange = async (
    candidateId: string,
    status: string,
  ) => {
    try {
      await updateCandidateStatus(candidateId, status);

      setCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === candidateId
            ? {
                ...candidate,
                status,
              }
            : candidate,
        ),
      );
    } catch (err) {
      setErrorState(
        "candidates",
        err instanceof Error
          ? err.message
          : "Failed to update candidate status.",
      );
    }
  };

  // Only the visible page is eligible for bulk selection; ZIP downloads are
  // intentionally limited to 20 candidates as in the existing implementation.
  const MAX_SELECTED_CANDIDATES = 20;
  const selectablePageIds = sortedCandidates
    .slice(0, MAX_SELECTED_CANDIDATES)
    .map(candidate => candidate.id);
  const selectedVisibleCount = selectablePageIds.filter(id =>
    selectedCandidates.includes(id)
  ).length;
  const allVisibleSelected = selectablePageIds.length > 0 &&
    selectedVisibleCount === selectablePageIds.length;

  const handleCandidateSelection = (candidateId: string, checked: boolean) => {
    setSelectedCandidates(prev => {
      if (!checked) return prev.filter(id => id !== candidateId);
      if (prev.includes(candidateId)) return prev;
      if (prev.length >= MAX_SELECTED_CANDIDATES) {
        return prev;
      }
      return [...prev, candidateId];
    });
  };

  const handleSelectAllVisible = (checked: boolean) => {
    setSelectedCandidates(prev => {
      if (!checked) {
        // Deselect this page, without clearing selections on other pages.
        const visible = new Set(sortedCandidates.map(candidate => candidate.id));
        return prev.filter(id => !visible.has(id));
      }
      const available = MAX_SELECTED_CANDIDATES - prev.length;
      if (available <= 0) return prev;
      const additions = selectablePageIds
        .filter(id => !prev.includes(id))
        .slice(0, available);
      return [...prev, ...additions];
    });
  };

  const handleDownloadCV = async (candidate: Candidate) => {
    try {
      setDownloadingCandidateId(candidate.id);
      setLoadingState("cv", true);
      setErrorState("cv", "");

      const response = await getCandidateCV(candidate.id);
      const blob = response.data;

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `CV_${candidate.name || candidate.id}`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) fileName = match[1];
      }

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorState(
        "cv",
        err instanceof Error ? err.message : "Failed to download CV.",
      );
    } finally {
      setDownloadingCandidateId(null);
      setLoadingState("cv", false);
    }
  };

  const handleBulkDownload = async () => {
    if (!jobDetail || selectedCandidates.length === 0) return;

    try {
      setLoadingState("cv", true);
      setErrorState("cv", "");

      const response = await downloadSelectedCVs(
        jobDetail.jobId,
        selectedCandidates,
      );
      const blob = response.data;

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Selected_CVs.zip";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorState(
        "cv",
        err instanceof Error ? err.message : "Failed to download selected CVs.",
      );
    } finally {
      setLoadingState("cv", false);
    }
  };

  // CV files are fetched only on demand. One inline preview stays active at a time,
  // while any number of application detail rows may remain expanded for comparison.
  const handlePreviewCV = async (candidate: Candidate) => {
    if (previewCandidateId === candidate.id) {
      previewRequestId.current += 1;
      setPreviewCandidateId(null);
      setPreviewUrl("");
      setPreviewError("");
      setPreviewLoading(false);
      return;
    }

    const requestId = ++previewRequestId.current;
    setExpandedCandidateIds(prev => prev.includes(candidate.id) ? prev : [...prev, candidate.id]);
    setPreviewCandidateId(candidate.id);
    setPreviewUrl("");
    setPreviewMimeType("");
    setPreviewError("");
    setPreviewLoading(true);

    try {
      const response = await previewCandidateCV(candidate.id);
      if (requestId !== previewRequestId.current) return;
      const blob: Blob = response.data;
      const type = (blob.type || response.headers["content-type"] || "").toLowerCase();
      setPreviewMimeType(type);
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      if (requestId !== previewRequestId.current) return;
      console.error("Failed to preview CV:", err);
      setPreviewError("This CV could not be previewed. You can still download it.");
    } finally {
      if (requestId === previewRequestId.current) setPreviewLoading(false);
    }
  };

  const renderInlineCV = (candidate: Candidate) => {
    if (previewCandidateId !== candidate.id) return null;
    const canEmbed = previewMimeType.includes("pdf") || previewMimeType.startsWith("image/");

    return (
      <Box sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", overflow: "hidden" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" fontWeight={700}>CV preview — {candidate.name}</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Download />} onClick={() => handleDownloadCV(candidate)}>Download</Button>
            <IconButton size="small" aria-label="Close CV preview" onClick={() => {
              previewRequestId.current += 1;
              setPreviewCandidateId(null);
              setPreviewUrl("");
              setPreviewLoading(false);
            }}><Close fontSize="small" /></IconButton>
          </Stack>
        </Stack>
        {previewLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, p: 4 }}>
            <CircularProgress size={20} /><Typography variant="body2">Loading CV…</Typography>
          </Box>
        ) : previewError ? (
          <Alert severity="warning" sx={{ m: 2 }}>{previewError}</Alert>
        ) : previewUrl && canEmbed ? (
          <Box sx={{ height: { xs: 360, md: 520 }, bgcolor: "grey.50" }}>
            {previewMimeType.startsWith("image/") ? (
              <Box component="img" src={previewUrl} alt={`CV of ${candidate.name}`} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <iframe src={previewUrl} title={`CV of ${candidate.name}`} width="100%" height="100%" style={{ border: 0 }} />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Inline preview is available for PDF and image CVs. Please download this file to view it.
          </Typography>
        )}
      </Box>
    );
  };

  // Compact job-wide summary; values come from the candidates API.
  const renderMetricCard = (
    label: string,
    value: string | number,
    helper?: string,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        px: { xs: 1, sm: 1.5 },
        py: 1,
        minHeight: 60,
        height: "100%",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ lineHeight: 1.35, mt: 0.25, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Typography>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          {helper}
        </Typography>
      )}
    </Paper>
  );

  const renderJobSummary = () => {
    if (!jobDetail) return null;

    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={900}>
                {jobDetail.jobTitle || "Untitled Job"}
              </Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.7}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {jobDetail.location || "No location"}
                  </Typography>
                </Stack>

                {jobDetail.deadline && (
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    <Event fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Deadline:{" "}
                      {new Date(jobDetail.deadline).toLocaleDateString()}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            <Chip
              label={`Job: ${jobDetail.status || "No Status"}`}
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          {jobDetail.AdditionalInformation && (
            <Typography variant="body2" color="text.secondary">
              {jobDetail.AdditionalInformation}
            </Typography>
          )}

          {evaluationCriteria.length > 0 && (
            <>
              <Divider />

              <Box>
                <Button
                  size="small"
                  color="inherit"
                  aria-expanded={criteriaOpen}
                  onClick={() => setCriteriaOpen(prev => !prev)}
                  endIcon={<ExpandMore sx={{ transform: criteriaOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />}
                  sx={{ textTransform: "none", fontWeight: 700, mb: criteriaOpen ? 1 : 0 }}
                >
                  Evaluation criteria ({evaluationCriteria.length})
                </Button>
                <Collapse in={criteriaOpen} unmountOnExit>
                <Grid container spacing={1.5}>
                  {evaluationCriteria.map((criterion, index) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      key={`${criterion.name}-${index}`}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "background.default",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={0.5}
                        >
                          <Typography variant="body2" fontWeight={700}>
                            {criterion.name}
                          </Typography>

                          <Chip
                            size="small"
                            label={formatWeight(criterion.weight)}
                          />
                        </Stack>

                        {criterion.description && (
                          <Typography variant="caption" color="text.secondary">
                            {criterion.description}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                </Collapse>
              </Box>
            </>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderCandidateDetails = (candidate: Candidate) => (
    <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2, bgcolor: "background.default" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Application review — {candidate.name}— {candidate.location} </Typography>
        <Stack direction="row" gap={1}>
          <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => handlePreviewCV(candidate)} disabled={previewLoading && previewCandidateId === candidate.id}>
            {previewCandidateId === candidate.id ? "Hide CV" : "View CV"}
          </Button>
          <Button size="small" variant="text" startIcon={<Download />} onClick={() => handleDownloadCV(candidate)}>Download CV</Button>
        </Stack>
      </Stack>
      <Grid container spacing={2}>
        {/* Smaller first column */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Work fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={900}>
              Professional Summary
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="body2">
              <b>Highest Education:</b> {candidate.highestEducation || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Current Organization:</b>{" "}
              {candidate.currentOrganization || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Current Designation:</b> {candidate.currentDesignation || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Total Experience:</b> {candidate.totalExperience || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Relevant Experience:</b> {candidate.relevantExperience || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Key Expertise:</b> {candidate.keyExpertise || "-"}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle2" fontWeight={900}>
              Compensation
            </Typography>

            <Typography variant="body2">
              <b>Current Salary:</b> {candidate.currentSalary || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Benefits:</b> {candidate.benefits || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Expected Salary:</b> {candidate.expectedSalary || "-"}
            </Typography>
          </Stack>
          </Paper>
        </Grid>

        {/* Wider answers column */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Article fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={900}>
              Screening Answers
            </Typography>
          </Stack>

          {(() => {
            // Show available answers even if the job-detail request is delayed
            // or its screening-question metadata is unavailable.
            const questions = jobDetail?.screeningQuestions ?? [];
            const answers = normalizeAnswers(candidate.answers);
            const count = Math.max(questions.length, answers.length);

            if (count === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  No screening questions or answers were provided.
                </Typography>
              );
            }

            return (
              <Stack spacing={1}>
                {Array.from({ length: count }, (_, index) => (
                  <Box
                    key={`${candidate.id}-answer-${index}`}
                    sx={{ pb: 1, borderBottom: index < count - 1 ? "1px solid" : "none", borderColor: "divider" }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {`Q${index + 1}. ${questions[index] || "Screening question"}`}
                    </Typography>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
                      {answers[index]?.trim() || "No answer provided."}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            );
          })()}
          </Paper>
        </Grid>

        {/* Ranking column */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <SmartToyIcon fontSize="small" color="success" />
            <Typography variant="subtitle2" fontWeight={900}>
              AI Ranking
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {candidate.rankingJustification ||
              "Ranking has not been generated yet."}
          </Typography>

          {evaluationCriteria.length > 0 ? (
            <Stack spacing={1.4}>
              {evaluationCriteria.map((criterion) => {
                const score = getScoreValue(candidate, criterion.name);

                return (
                  <Box key={`${candidate.id}-${criterion.name}`}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography variant="caption">
                        {criterion.name}
                      </Typography>
                      <Typography variant="caption" fontWeight={800}>
                        {score}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={Math.min(score, 100)}
                      sx={{ height: 7, borderRadius: 99, mt: 0.5 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Evaluation criteria are not available yet.
            </Typography>
          )}

          {/* <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
          {[
            { label: "Shortlist", status: "Shortlisted", color: "primary" },
            { label: "Finalize", status: "Finalized", color: "success" },
            { label: "Reject", status: "Rejected", color: "error" },
          ].map((item) => (
            <Button
              key={item.status}
              size="small"
              variant={candidate.status === item.status ? "contained" : "outlined"}
              color={item.color as "primary" | "success" | "error"}
              disabled={candidate.status === item.status}
              onClick={() => handleCandidateStatusChange(candidate.id, item.status)}
            >
              {item.label}
            </Button>
          ))}
        </Stack> */}
          </Paper>
        </Grid>
      </Grid>
      {renderInlineCV(candidate)}
    </Box>
  );

  const renderCandidateRow = (candidate: Candidate, _index: number) => {
    const expanded = expandedCandidateIds.includes(candidate.id);

    return (
      <React.Fragment key={candidate.id}>
        <TableRow hover sx={{ "& > td": { verticalAlign: "middle" } }}>
          <TableCell padding="checkbox" align="center" sx={{ width: 48, p: "4px !important", verticalAlign: "middle" }}>
            <Checkbox
              size="small"
              sx={{ p: 0.5 }}
              checked={selectedCandidates.includes(candidate.id)}
              disabled={!selectedCandidates.includes(candidate.id) && selectedCandidates.length >= 20}
              inputProps={{ "aria-label": `Select ${candidate.name || "candidate"}` }}
              onChange={(event) =>
                handleCandidateSelection(candidate.id, event.target.checked)
              }
            />
          </TableCell>

          <TableCell sx={{ overflowWrap: "anywhere", verticalAlign: "middle" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {candidate.name || "Unknown"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {candidate.email || "No email"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {candidate.phone || "No phone"}
                </Typography>
              </Box>
            </Stack>
          </TableCell>
          <TableCell sx={{ overflowWrap: "anywhere", verticalAlign: "middle" }}>
            <Typography variant="body2">
              {candidate.location || "-"}
              </Typography>
          </TableCell>

          <TableCell sx={{ overflowWrap: "anywhere", verticalAlign: "middle" }}>
            <Typography variant="body2" fontWeight={700}>
              {candidate.currentDesignation || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {candidate.currentOrganization || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {candidate.highestEducation || "-"}
            </Typography>
          </TableCell>

          <TableCell sx={{ overflowWrap: "anywhere", verticalAlign: "middle" }}>
            <Typography variant="body2">
              Total: <b>{candidate.totalExperience || "-"}</b>
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Relevant: {candidate.relevantExperience || "-"}
            </Typography>
          </TableCell>

          <TableCell sx={{ overflowWrap: "anywhere", verticalAlign: "middle" }}>
            <Tooltip title={candidate.keyExpertise || ""}>
              <Typography
                variant="body2"
                sx={{
                  overflowWrap: "anywhere",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {candidate.keyExpertise || "-"}
              </Typography>
            </Tooltip>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Expected: {candidate.expectedSalary || "-"}
            </Typography>
          </TableCell>

          {/* <TableCell sx={{ minWidth: 150 }}>
            <Chip
              size="small"
              label={candidate.status || "Applied"}
              color={getApplicationStageColor(candidate.status)}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </TableCell> */}

          <TableCell align="center" sx={{ verticalAlign: "middle" }}>
            {getRankingChip(candidate)}
          </TableCell>

          <TableCell align="center" sx={{ verticalAlign: "middle" }}>
            <Stack direction="row" spacing={0} justifyContent="center" flexWrap="nowrap">
              {candidate.cvFilePath && (
                <Tooltip title="Download CV">
                  <IconButton
                    size="small"
                    onClick={() => handleDownloadCV(candidate)}
                    disabled={
                      loading.cv && downloadingCandidateId === candidate.id
                    }
                  >
                    {loading.cv && downloadingCandidateId === candidate.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Download fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Preview CV">
                <IconButton
                  size="small"
                  onClick={() => handlePreviewCV(candidate)}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={expanded ? "Hide details" : "View details"}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setExpandedCandidateIds(prev => expanded
                      ? prev.filter(id => id !== candidate.id)
                      : [...prev, candidate.id])
                  }
                >
                  <ExpandMore
                    fontSize="small"
                    sx={{
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={8}
            sx={{ p: 0, borderBottom: expanded ? undefined : "none" }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {renderCandidateDetails(candidate)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const renderTableHeader = () => {
    const headerCells: {
      key?: OrderBy;
      label: string;
      align?: "left" | "center" | "right";
    }[] = [
      { key: "name", label: "Candidate" },
      { key: "location", label: "Location" },
      { key: "currentDesignation", label: "Professional Summary" },
      { label: "Experience" },
      { label: "Expertise / Salary" },
      // { key: "status", label: "Application Stage" },
      { key: "totalScore", label: "AI Ranking", align: "center" },
      { label: "Actions", align: "center" },
    ];

    return (
      <TableHead>
        <TableRow sx={{ bgcolor: "background.default" }}>
          <TableCell padding="checkbox" align="center" sx={{ width: 48, p: "4px !important", verticalAlign: "middle" }}>
            <Checkbox
              size="small"
              sx={{ p: 0.5 }}
              checked={allVisibleSelected}
              indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
              onChange={(event) => handleSelectAllVisible(event.target.checked)}
              inputProps={{ "aria-label": "Select or deselect up to 20 candidates on this page" }}
            />
          </TableCell>

          {headerCells.map((cell) => (
            <TableCell
              key={cell.label}
              align={cell.align || "left"}
              sx={{ fontWeight: 800, whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25, px: 1 }}
            >
              {cell.key ? (
                <TableSortLabel
                  active={orderBy === cell.key}
                  direction={orderBy === cell.key ? order : "asc"}
                  onClick={() => handleSort(cell.key!)}
                >
                  {cell.label}
                </TableSortLabel>
              ) : (
                cell.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={700} letterSpacing="-0.03em">
            Candidates
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Select a job, review applications, and compare AI ranking results.
            {/* , and move candidates through the hiring process. */}
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Business color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Job Selection
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Filter by job status, then search and select a job title.
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Job Status
                  </Typography>

                  <Select
                    value={jobStatusFilter}
                    onChange={handleJobStatusChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select status</em>
                    </MenuItem>
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="In Review">In Review</MenuItem>
                    <MenuItem value="Reviewed">Reviewed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={9}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Search Job Title
                </Typography>

                <Autocomplete
                  options={jobsList}
                  filterOptions={jobFilter}
                  loading={loading.jobs}
                  disabled={!jobStatusFilter || loading.jobs}
                  value={
                    jobsList.find((job) => job.jobId === selectedJobId) || null
                  }
                  inputValue={selectedJobInput}
                  onInputChange={(_, value) => setSelectedJobInput(value)}
                  onChange={(_, value) => handleJobChange(value)}
                  getOptionLabel={(job) => job?.jobTitle || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.jobId === value.jobId
                  }
                  openOnFocus
                  clearOnBlur={false}
                  noOptionsText={
                    jobStatusFilter
                      ? "No matching jobs found"
                      : "Select job status first"
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.jobId}>
                      <Stack spacing={0.2}>
                        <Typography variant="body2" fontWeight={700}>
                          {option.jobTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.status}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        jobStatusFilter
                          ? "Type job title to search..."
                          : "Select job status first"
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Search
                                color={jobStatusFilter ? "primary" : "disabled"}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loading.jobs ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {error.jobs && <Alert severity="error">{error.jobs}</Alert>}

            {loading.jobDetail ? (
              <Box>
                <Skeleton variant="text" height={40} width="50%" />
                <Skeleton variant="text" height={22} width="75%" />
                <Skeleton variant="text" height={22} width="35%" />
              </Box>
            ) : error.jobDetail ? (
              <Alert severity="error">{error.jobDetail}</Alert>
            ) : jobDetail ? (
              renderJobSummary()
            ) : (
              <Alert severity="info">
                {jobStatusFilter
                  ? "Search and select a job to view candidate applications."
                  : "Start by selecting a job status."}
              </Alert>
            )}
          </Stack>
        </Paper>

        {selectedJobId && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "background.paper"
              }}
            >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    Applications
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Candidate applications for{" "}
                    {jobDetail?.jobTitle ||
                      jobsList.find(j => j.jobId === selectedJobId)?.jobTitle ||
                      "selected job"}
                  </Typography>
                </Box>

                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => setRefreshKey(v => v + 1)}
                    disabled={loading.candidates}
                  >
                    Refresh
                </Button>
              </Stack>

              <Grid container spacing={0.75} sx={{ maxWidth: { md: 580 } }}>
                <Grid item xs={4} sm={3} md={4}>
                  {renderMetricCard("Total", stats.total)}
                </Grid>
                <Grid item xs={4} sm={3} md={4}>
                  {renderMetricCard("Ranked", stats.ranked)}
                </Grid>
                <Grid item xs={4} sm={3} md={4}>
                  {renderMetricCard("Pending Ranking", stats.pendingRanking)}
                </Grid>
                {/* <Grid item xs={6} md={2}>
                  {renderMetricCard("Shortlisted", stats.shortlisted)}
                </Grid>
                <Grid item xs={6} md={2}>
                  {renderMetricCard("Rejected", stats.rejected)}
                </Grid> */}
                {/* <Grid item xs={6} md={2}>
                  {renderMetricCard("Avg. Score", `${stats.averageScore}/100`)}
                </Grid> */}
              </Grid>

              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 3, bgcolor: "background.default" }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ minWidth: 0, flexWrap: { md: "wrap", lg: "nowrap" } }}
                >
                  <TextField
                    size="small"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search candidates by name, location, email or expertise"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{ flex: "1 1 400px", minWidth: 0, "& .MuiInputBase-root": { minWidth: 0 }, "& input": { minWidth: 0, textOverflow: "clip" } }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flexShrink: 0 }}>
                    {/* <FormControl size="small" sx={{ minWidth: 170 }}>
                      <Select
                        value={candidateStatusFilter}
                        onChange={(event) => {
                          setCandidateStatusFilter(event.target.value);
                          setPaginationData((prev) => ({ ...prev, pageNumber: 1 }));
                        }}
                      >
                        <MenuItem value="All">All Application Stages</MenuItem>
                        <MenuItem value="Applied">Applied</MenuItem>
                        <MenuItem value="Ranked">Ranked</MenuItem>
                        <MenuItem value="Shortlisted">Shortlisted</MenuItem>
                        <MenuItem value="Finalized">Finalized</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                        <MenuItem value="Ranking Failed">Ranking Failed</MenuItem>
                      </Select>
                    </FormControl> */}

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={paginationData.pageSize}
                        onChange={(event) =>
                          setPaginationData((prev) => ({
                            ...prev,
                            pageSize: Number(event.target.value),
                            pageNumber: 1,
                          }))
                        }
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <MenuItem key={size} value={size}>
                            {size} rows
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button variant="outlined" onClick={resetCandidateFilters}>
                      Reset
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {error.candidates && (
                <Alert severity="error">{error.candidates}</Alert>
              )}
              {error.cv && <Alert severity="error">{error.cv}</Alert>}

              {loading.candidates ? (
                <Stack spacing={1}>
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rectangular"
                      height={64}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Stack>
              ) : candidates.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Person
                    sx={{ fontSize: 72, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="h6" fontWeight={900}>
                    No applications yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Candidate applications for this job will appear here.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: "100%", minWidth: 0, overflowX: { xs: "auto", lg: "visible" } }}>
                  <Table size="small" sx={{ width: "100%", minWidth: { xs: 860, lg: 0 }, tableLayout: "fixed", "& .MuiTableCell-root": { px: { xs: 0.75, md: 1 }, py: 1 }, "& .MuiTableHead-root .MuiTableCell-root": { verticalAlign: "middle" } }}>
                    <colgroup>
                      <col style={{ width: "48px" }} />
                      <col style={{ width: "19%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "19%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "17%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "14%" }} />
                    </colgroup>
                    {renderTableHeader()}

                    <TableBody>
                      {sortedCandidates.map((candidate, index) =>
                        renderCandidateRow(candidate, index),
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {candidates.length > 0 && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ pt: 1 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBack />}
                    disabled={!paginationData.hasPreviousPage}
                    onClick={() =>
                      setPaginationData((prev) => ({
                        ...prev,
                        pageNumber: Math.max(prev.pageNumber - 1, 1),
                      }))
                    }
                  >
                    Previous
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    Page {paginationData.pageNumber} of{" "}
                    {paginationData.totalPages || 1} ·{" "}
                    {paginationData.totalCount} total candidates
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForward />}
                    disabled={!paginationData.hasNextPage}
                    onClick={() =>
                      setPaginationData((prev) => ({
                        ...prev,
                        pageNumber: prev.pageNumber + 1,
                      }))
                    }
                  >
                    Next
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        {selectedCandidates.length > 0 && (
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1300,
              px: 2,
              py: 1.2,
              borderRadius: 99,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "grey.900",
              color: "white",
            }}
          >
            <Typography variant="body2" fontWeight={900}>
              {selectedCandidates.length} selected
            </Typography>

            <Button
              size="small"
              variant="contained"
              startIcon={<Download />}
              onClick={handleBulkDownload}
              disabled={loading.cv}
            >
              Download ZIP
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedCandidates([])}
              sx={{ color: "white", borderColor: "rgba(255,255,255,0.45)" }}
            >
              Clear
            </Button>
          </Paper>
        )}


      </Stack>
    </Box>
  );
};

export default CandidateTrackingPage;